import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { PremiumLock } from "@/components/Upgrade";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/plant";
import { CalendarRange, Sprout, Home, ArrowRight, Scissors, Snowflake, Sun, Leaf, Info, Loader2, MapPin, Wand2 } from "lucide-react";
import { toast } from "sonner";

function DateStep({ icon: Icon, label, value, tint }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className="text-sm font-semibold">{value ? formatDate(value) : "—"}</p>
      </div>
    </div>
  );
}

export default function PlantingCalendar() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [frostDate, setFrostDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [locQuery, setLocQuery] = useState("");
  const [country, setCountry] = useState("");
  const [detecting, setDetecting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [settings, cal] = await Promise.all([api.get("/settings"), api.get("/planting-calendar")]);
    setFrostDate(settings.data?.last_frost_date || "");
    setData(cal.data);
  }, []);

  useEffect(() => {
    if (user && user.is_premium) load();
  }, [load, user]);

  const detect = async () => {
    if (!locQuery.trim()) return;
    setDetecting(true);
    try {
      const { data } = await api.post("/frost/detect", { query: locQuery, country });
      if (!data.frost_detected) {
        toast.info(data.message || "No frost found for that location");
      } else {
        setFrostDate(data.last_frost_date);
        await api.put("/settings", { last_frost_date: data.last_frost_date });
        const cal = await api.get("/planting-calendar");
        setData(cal.data);
        toast.success(`${data.place}: last frost ~${new Date(data.last_frost_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} (avg of ${data.years_analyzed} yrs)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not detect frost date");
    } finally {
      setDetecting(false);
    }
  };

  if (user && !user.is_premium) {
    return (
      <Layout>
        <PremiumLock
          title="Planting Calendar is a Pro feature"
          description="Get the best sow, transplant and harvest dates for every sprout, tailored to your frost date."
          features={["Per-sprout sow & transplant timing", "Harvest estimates from grow guides", "Unlimited sprouts & all AI tools"]}
        />
      </Layout>
    );
  }

  const saveFrost = async () => {
    if (!frostDate) return;
    setSaving(true);
    try {
      await api.put("/settings", { last_frost_date: frostDate });
      const { data } = await api.get("/planting-calendar");
      setData(data);
      toast.success("Calendar updated for your frost date");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Planting Calendar</h1>
        <p className="text-muted-foreground mt-1">Best sow &amp; transplant dates for each sprout, based on its grow guide.</p>
      </div>

      <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm mb-6" data-testid="frost-setter">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1 space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Snowflake className="h-4 w-4 text-sky-500" /> Your last spring frost date
            </Label>
            <Input type="date" value={frostDate} onChange={(e) => setFrostDate(e.target.value)} className="max-w-xs" data-testid="frost-date-input" />
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <Info className="h-3.5 w-3.5" /> All dates are calculated relative to this. Search "[your area] last frost date" if unsure.
            </p>
          </div>
          <Button onClick={saveFrost} disabled={saving || !frostDate} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="save-frost-button">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Update calendar
          </Button>
        </div>

        <div className="mt-5 pt-5 border-t border-border">
          <Label className="flex items-center gap-1.5 mb-2">
            <Wand2 className="h-4 w-4 text-emerald-600" /> Auto-detect from your location
          </Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={locQuery} onChange={(e) => setLocQuery(e.target.value)} placeholder="City, country  (or postal code)" className="pl-9" data-testid="frost-location-input" onKeyDown={(e) => e.key === "Enter" && detect()} />
            </div>
            <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country code for postal (e.g. US)" className="sm:w-56" data-testid="frost-country-input" />
            <Button onClick={detect} disabled={detecting || !locQuery.trim()} variant="secondary" className="rounded-xl shrink-0" data-testid="detect-frost-button">
              {detecting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />}
              Detect
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <Info className="h-3.5 w-3.5" /> We estimate your average last frost from 6 years of local weather history.
          </p>
        </div>
      </div>

      {!data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[0, 1].map((i) => (
            <div key={i} className="h-40 rounded-2xl bg-card border border-border animate-pulse" />
          ))}
        </div>
      ) : data.needs_frost_date ? (
        <div className="bg-card rounded-2xl border border-dashed border-border p-12 text-center" data-testid="calendar-empty">
          <CalendarRange className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-muted-foreground">Set your last frost date above to generate your planting calendar.</p>
        </div>
      ) : data.items.length === 0 && data.unmatched.length === 0 ? (
        <div className="bg-card rounded-2xl border border-dashed border-border p-12 text-center">
          <Sprout className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-muted-foreground">Add sprouts to see their planting schedule here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="calendar-grid">
            {data.items.map((it) => (
              <div key={it.plant_id} className="bg-card rounded-2xl border border-border p-5 shadow-sm hover:shadow-md transition animate-rise" data-testid="calendar-item">
                <div className="flex items-start justify-between gap-2 mb-4">
                  <button onClick={() => navigate(`/sprouts/${it.plant_id}`)} className="text-left">
                    <h3 className="font-display font-semibold hover:text-emerald-700">{it.name}</h3>
                    <p className="text-xs text-muted-foreground">{it.species} · {it.days_to_harvest} days to harvest</p>
                  </button>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {it.sow_date == null ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                        <Snowflake className="h-3.5 w-3.5" /> Autumn planting
                      </span>
                    ) : (
                      <>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${it.method === "indoors" ? "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300" : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"}`}>
                          {it.method === "indoors" ? <Home className="h-3.5 w-3.5" /> : <Sprout className="h-3.5 w-3.5" />}
                          {it.method === "indoors" ? "Start indoors" : "Direct sow"}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${it.season === "warm" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300" : "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300"}`}>
                          {it.season === "warm" ? <Sun className="h-3 w-3" /> : <Snowflake className="h-3 w-3" />} {it.season}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {it.note ? (
                  <p className="text-sm bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 rounded-xl p-3 flex gap-2">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" /> {it.note}
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <DateStep icon={Sprout} label="Sow" value={it.sow_date} tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" />
                    {it.method === "indoors" && (
                      <DateStep icon={Leaf} label="Transplant" value={it.transplant_date} tint="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" />
                    )}
                    <DateStep icon={Scissors} label="Harvest ~" value={it.harvest_date} tint="bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {data.unmatched.length > 0 && (
            <div className="bg-card rounded-2xl border border-border p-5 shadow-sm">
              <h2 className="font-display font-semibold mb-2 text-sm text-muted-foreground uppercase tracking-wider">No grow guide yet</h2>
              <div className="flex flex-wrap gap-2">
                {data.unmatched.map((u) => (
                  <button key={u.plant_id} onClick={() => navigate(`/sprouts/${u.plant_id}`)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition" data-testid="calendar-unmatched">
                    {u.name} <span className="text-xs text-muted-foreground">{u.species || "—"}</span> <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
